import { ColumnDefResolved, OnChangeFn, SortingState, Updater } from "@tanstack/react-table"
import { useEffect, useMemo, useState } from "react"

import extractURLSearchParam from "@utils/extractURLSearchParam"
import getSortingStateFromOrdering from "@utils/getSortingStateFromOrdering"
import removeURLSearchParam from "@utils/removeURLSearchParam"

import { UseObjectsViewSortingStateProps } from "@organisms/ObjectsView/ObjectsView.types"

import { LIST_SEARCH_PARAM_ORDERING } from "@constants/searchParams"

export default function useObjectsViewSortingState<T>(props: UseObjectsViewSortingStateProps) {
    const { activeTab, savedSorting, saveSorting } = props

    const [sortingChanged, setSortingChanged] = useState(false)

    // The param stays in the URL until it has been written to a view config, so a provider that mounts
    // before the tab is known and is replaced by another still hands the sort on instead of losing it.
    const sortingOverride = useMemo(() => extractURLSearchParam(LIST_SEARCH_PARAM_ORDERING, false), [])
    const overrideSorting = useMemo(
        () => (sortingOverride ? getSortingStateFromOrdering(sortingOverride) : undefined),
        [sortingOverride],
    )
    const defaultOrdering = useMemo(
        () => activeTab?.endpointKwargs?.find((kwarg) => kwarg[0] === "ordering")?.[1] || "",
        [activeTab],
    )

    // A shared `?ordering=` link wins until it has been persisted, so the query is right on the
    // first render instead of after the write has travelled back through storage. The flag has to
    // be state rather than a ref: once the override is saved it must stop winning, otherwise it
    // would override every sort the user picks for the rest of the page's life.
    const [isSortingOverrideApplied, setIsSortingOverrideApplied] = useState(false)

    // The active view's saved config is what its other settings are keyed on, so reading the ordering
    // from it is what keeps a tab's sort from leaking into its siblings, or into another view of the
    // same tab. An empty saved value means the user has not sorted this view of the tab yet, and the
    // tab's own default takes over.
    const sorting =
        (!isSortingOverrideApplied && overrideSorting) ||
        (savedSorting?.length ? savedSorting : getSortingStateFromOrdering(defaultOrdering as string))

    const onSortingChange: OnChangeFn<SortingState> = (newSorting: Updater<SortingState>) => {
        if (typeof newSorting === "function") {
            newSorting = newSorting(sorting)
        }
        saveSorting?.(newSorting)
        setSortingChanged(true)
    }

    // The override seeds the view of the tab it opens rather than the page, so it has to be written to
    // that view's saved config — and only once the active tab is known, since the config is keyed by
    // it. Consuming the param at that point keeps a later view switch from applying it again. A view
    // with nowhere to save it keeps the override for as long as the page lives.
    useEffect(() => {
        if (overrideSorting && activeTab && saveSorting && !isSortingOverrideApplied) {
            saveSorting(overrideSorting)
            removeURLSearchParam(LIST_SEARCH_PARAM_ORDERING)
            setIsSortingOverrideApplied(true)
        }
    }, [overrideSorting, activeTab, isSortingOverrideApplied])

    const setCurrentSortToAscending = () => {
        onSortingChange([{ id: sorting[0].id, desc: false }])
    }

    const setCurrentSortToDescending = () => {
        onSortingChange([{ id: sorting[0].id, desc: true }])
    }

    const onSortingItemSelection = (column: ColumnDefResolved<T>) => {
        const notSelected = sorting[0]?.id !== column.accessorKey

        if (notSelected) {
            onSortingChange([
                {
                    id: column.accessorKey as string,
                    desc: false,
                },
            ])
        }
    }

    return {
        sorting,
        sortingChanged,
        setSortingChanged,
        onSortingChange,
        setCurrentSortToAscending,
        setCurrentSortToDescending,
        onSortingItemSelection,
    }
}
